import { useState } from 'react';
import { useMutation } from 'convex/react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../convex/_generated/api';
import './CommunityShareModal.css';

/**
 * Reusable modal for sharing content to the community from any studio.
 * Props:
 *  - projectId: linked project
 *  - initialBody: pre-filled text
 *  - imageStorageId: optional image to attach (from marketing visual assets)
 *  - audioStorageId: optional audio to attach (from audiobook)
 *  - genre: project genre for auto-tagging
 *  - onClose: close handler
 *  - onSuccess: callback after successful post
 */
function CommunityShareModal({
  projectId,
  initialBody = '',
  imageStorageId,
  audioStorageId,
  genre,
  onClose,
  onSuccess,
}) {
  const { t } = useTranslation();
  const [body, setBody] = useState(initialBody);
  const [isPosting, setIsPosting] = useState(false);
  const [error, setError] = useState('');

  const createPost = useMutation(api.community.createPost);

  const postType = audioStorageId ? 'audio' : 'text';

  const handleSubmit = async () => {
    if (!body.trim()) {
      setError(t('community.writeSomethingBeforeSharing'));
      return;
    }
    setIsPosting(true);
    setError('');

    try {
      await createPost({
        type: postType,
        body: body.trim(),
        genreTags: genre ? [genre] : [],
        topicTags: [],
        linkedProjectId: projectId || undefined,
        imageId: imageStorageId || undefined,
        audioStorageId: audioStorageId || undefined,
      });
      onSuccess?.(t('community.sharedToCommunity'));
      onClose();
    } catch (err) {
      setError(err?.data?.message || err?.message || t('community.failedToShare'));
    } finally {
      setIsPosting(false);
    }
  };

  return (
    <div className="csm-overlay" onClick={onClose}>
      <div className="csm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="csm-header">
          <h3>{t('community.shareToCommunity')}</h3>
          <button className="csm-close" onClick={onClose}>&times;</button>
        </div>

        {error && <div className="csm-error">{error}</div>}

        <div className="csm-body">
          {audioStorageId && (
            <div className="csm-badge">{t('community.audioClipAttached')}</div>
          )}
          {imageStorageId && (
            <div className="csm-badge">{t('community.imageAttached')}</div>
          )}
          <textarea
            className="csm-textarea"
            rows={4}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={t('community.shareCaptionPlaceholder')}
            maxLength={2400}
          />
          <div className="csm-meta">
            <span>{body.length}/2400</span>
            {genre && <span className="csm-tag">{genre}</span>}
          </div>
        </div>

        <div className="csm-actions">
          <button className="btn btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={isPosting || !body.trim()}>
            {isPosting ? t('community.sharing') : t('community.shareToCommunity')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CommunityShareModal;
